// eslint-disable-next-line no-unused-vars
import React, {useState, useRef} from 'react';
import {Turnstile} from '@marsidev/react-turnstile';
import PageContentComponent from '../components/ui/pageContentComponent';
import PageContainerTitleComponent from '../components/ui/container/pageContainerTitleComponent';
import ErrorMessageComponent from '../components/ui/messageErrorComponent';
import {useTranslation} from 'react-i18next';
import {FaUser, FaLock} from 'react-icons/fa';
import {Button} from 'react-bootstrap';
import {NavLink, useNavigate} from 'react-router-dom';
import styled from 'styled-components';
import {useForm} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {useAuth} from '../hooks/useAuth';
import i18n from '../i18n';

// Yup-Schema mit i18next-Fehlermeldungen
const schema = yup.object().shape({
  username: yup
    .string()
    .min(5, i18n.t('loginForm.usernameMin'))
    .max(12, i18n.t('loginForm.usernameMax'))
    .required(i18n.t('loginForm.usernameRequired')),
  password: yup.string().min(6, i18n.t('loginForm.passwordMin')).required(i18n.t('loginForm.passwordRequired')),
  turnstileToken: yup.string().required(i18n.t('registerPage.turnstileRequired')),
});

const LoginPage = () => {
  const {t} = useTranslation();
  const {login} = useAuth();
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    setValue,
    formState: {errors},
  } = useForm({resolver: yupResolver(schema)});
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const turnstileRef = useRef(null);

  const onSubmit = async data => {
    setLoading(true);
    try {
      const response = await login(data);
      if (response.success) {
        navigate('/dashboard'); // Login erfolgreich → Weiterleitung
      } else {
        // Fehler ins URL-Query für SystemModal setzen
        setErrorMessage(response.error);
        // Captcha-Token zurücksetzen
        setValue('turnstileToken', '');
        if (turnstileRef.current) {
          turnstileRef.current.reset();
        }

        setTimeout(() => {
          setErrorMessage('');
        }, 10000);
      }
    } catch (error) {
      setErrorMessage(t('loginForm.unexpectedError'));
      // Captcha-Token zurücksetzen
      setValue('turnstileToken', '');
      if (turnstileRef.current) {
        turnstileRef.current.reset();
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageContentComponent>
      <PageContainerTitleComponent title={t('loginForm.pageTitle')} />
      <LoginContainer>
        {errorMessage && <ErrorMessageComponent message={errorMessage} />}
        <form onSubmit={handleSubmit(onSubmit)}>
          <InputContainer>
            <Label>{t(`loginForm.username`)}</Label>
            <Icon>
              <FaUser />
            </Icon>
            <InputField
              name="username_login"
              placeholder={t('loginForm.usernamePlaceholder')}
              type="text"
              {...register('username')}
              autoComplete="off"
            />
          </InputContainer>
          <InputContainer>
            <div style={{position: 'absolute', top: '100%', left: 0, width: '100%'}}>
              {errors['username'] && <ErrorMessageComponent message={errors['username'].message} />}
            </div>
            <Label>{t(`loginForm.password`)}</Label>
            <Icon>
              <FaLock />
            </Icon>
            <InputField
              name="password_login"
              placeholder={t('loginForm.passwordPlaceholder')}
              type="password"
              {...register('password')}
              autoComplete="off"
            />
            <div style={{position: 'absolute', top: '100%', left: 0, width: '100%'}}>
              {errors['password'] && <ErrorMessageComponent message={errors['password'].message} />}
            </div>
          </InputContainer>
          <CaptchaContainer>
            <Turnstile
              ref={turnstileRef}
              siteKey={process.env.REACT_APP_CLOUDFLARE_TURNSTILE_SITE_KEY}
              onSuccess={token => {
                setValue('turnstileToken', token);
              }}
            />
            {errors.turnstileToken && <ErrorMessageComponent message={errors.turnstileToken.message} />}
          </CaptchaContainer>
          <StyledButton type="submit" disabled={loading}>
            {loading ? t('loginForm.signingIn') : t('loginForm.signIn')}
          </StyledButton>
          <LoginOptions>
            <li>
              <StyledNavLink to="/forgot-password">{t('loginForm.forgotPassword')}</StyledNavLink>
            </li>
            <li>
              <StyledNavLink to="/register">
                {t('loginForm.noAccount')} <StyledSpan>{t('loginForm.registerNow')}</StyledSpan>
              </StyledNavLink>
            </li>
          </LoginOptions>
        </form>
      </LoginContainer>
    </PageContentComponent>
  );
};

export default LoginPage;

const LoginContainer = styled.div`
  max-width: 600px;
  margin: auto;
  padding: 50px;
  background-image: url('/assets/ui/container-big-bg.png');
  background-repeat: no-repeat;
  background-position: center;
  background-size: 100% 100%;

  @media (max-width: 768px) {
    padding: 40px;
  }
`;

const StyledButton = styled(Button)`
  display: block;
  margin: auto;
  width: 150px;
  max-width: 250px;
  border: none;
`;

const InputContainer = styled.div`
  position: relative;
  margin-bottom: 20px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: bold;
  margin-bottom: 5px;
  width: 100%;
  display: block;
`;

const InputField = styled.input`
  width: 100%;
  border-radius: 5px;
  display: block;
`;

const LoginOptions = styled.ul.attrs({className: 'text-center login-options'})`
  list-style: none;
  padding: 0;
  font-size: 11pt;
  @media (max-width: 768px) {
    font-size: 10pt;
    padding: 5px;
  }
`;

const StyledSpan = styled.span`
  color: #d85350;
  text-decoration: underline;
`;

const StyledNavLink = styled(NavLink)`
  color: #664c51;
  font-size: 9pt;
  text-decoration: none;
  &:hover {
    color: #d85350 !important;
  }
  @media (max-width: 768px) {
    font-size: 8pt;
  }
`;

const Icon = styled.div`
  position: absolute;
  left: 12px;
  top: 65%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  color: #888;
`;

const CaptchaContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 60px;
  flex-wrap: wrap;
  @media (max-width: 768px) {
    transform: scale(0.85);
  }
`;
